/**
 * @fileoverview The "Main Control Room" of our Nexme app
 * 
 * Think of this file like the main control room of a building. It's where all the
 * important decisions are made about what users see and where they can go.
 * It's like a smart receptionist who:
 * - Checks if you're allowed in (authentication)
 * - Shows you the right floor/room (routing)
 * - Displays important messages (notifications)
 * - Handles special requests (cookies, walkthroughs)
 * 
 * @author Nexme Development Team
 * @version 1.0.0
 */

// The basic React tools - like having a toolbox
import { useEffect, useState } from "react";                    // Tools for managing app state and timing
import { Route, Routes, useNavigate, useParams } from "react-router-dom"; // The "GPS system" for navigation
import { Navigate } from "react-router-dom";                   // The "redirect helper" - sends users to different pages

// The "security system" - checks who users are
import { useUserAuth } from "context/authProvider";

// The "pages" - different rooms in our building
import UserAuth from "pages/UserAuth";                         // The "login room" - where users sign in
import ActivateKey from "pages/ActivateKey";                   // The "activation room" - for new accounts
import HomeRoutes from "HomeRoutes";                           // The "main building" - all the main pages
import NotFoundPage from "pages/NotFoundPage";                 // The "lost and found" - when users go to wrong places
import Register from "pages/UserAuth/Register";                // The "registration desk" - for new users
import Walkthrough from "pages/walkthrough/Walkthrough";       // The "tour guide" - shows new users around
import HollandCodeQuiz from "pages/walkthrough/personalAI";    // The "personality test" - helps users discover themselves

// The "styling and fonts" - makes everything look nice
import "@fontsource-variable/heebo";                           // Our chosen font family

// The "analytics and tracking" - like security cameras that help us improve
import { clarity } from "react-microsoft-clarity";             // Microsoft's "analytics camera"

// The "notification system" - like a PA system for important messages
import { ToastContainer } from "react-toastify";               // The "message display system"
import "react-toastify/dist/ReactToastify.css";                // Styling for the message system

// The "loading screen" - what users see while things are loading
import Loader from "components/Loader";

// The "language and location helpers" - like having multilingual staff
import { useLocaleLanguage } from "hooks/useLocaleLanguage";   // The "translator" - handles different languages
import { useCheckTenant } from "hooks/user";                   // The "building manager" - checks which "apartment" we're in
import { useHubspotChat } from "hooks/hubspot";                // The "customer service chat" - for user support
import { t } from "i18next";                                   // The "translation helper"

// The "cookie consent" - like asking permission to use security cameras
import CookieConsent, { Cookies } from "react-cookie-consent"; // The "privacy permission system"

// The "memory system" - remembers app settings
import { useAppSelector } from "store/hooks";                  // The "memory reader" - gets data from our filing cabinet
import { getSettings } from "store/slices";                    // The "settings drawer" - specific app settings

// The "cleanup crew" - clears old data when needed
import { clearAllLocalStorage } from "utils/utility";          // The "janitor" - cleans up old stored data

/**
 * The "Welcome Back" Helper
 * 
 * Think of this like a friendly receptionist who greets you when you come back
 * from a special link (like an email confirmation). They:
 * - Take your special "ticket" (token) and save it
 * - Mark you as "checked in" (setUser)
 * - Show you to the main lobby (navigate to "/")
 * - Show a loading screen while they process everything
 */
const Redirect = () => {
  const navigate = useNavigate();           // The "usher" - shows you where to go
  const { token } = useParams();           // Your special "ticket" from the email
  const { setUser } = useUserAuth();       // The "check-in system" - marks you as logged in
  
  useEffect(() => {
    // Save your ticket and check you in, then show you to the main area
    localStorage.setItem("token", decodeURIComponent(token));
    setUser(decodeURIComponent(token));
    navigate("/");
  }, [token, navigate]);

  // Show a loading screen while processing your return
  return <Loader open={true} />;
};

/**
 * The "Goodbye" Helper
 * 
 * Think of this like a security guard who helps you check out when you're leaving.
 * They:
 * - Verify your identity (check if the token matches)
 * - Clean up your personal belongings (clear localStorage)
 * - Mark you as "checked out" (setUser to null)
 * - Show you to the exit (navigate to "/")
 * - Show a loading screen while they process everything
 */
const Logout = () => {
  // 1. HOOKS INITIALIZATION
  const navigate = useNavigate();           // React Router hook for programmatic navigation
  const { token } = useParams();           // Extracts URL parameter from route path
  const { setUser } = useUserAuth();       // Custom hook for user state management
  
  // 2. SIDE EFFECT EXECUTION
  useEffect(() => {
    // 3. TOKEN VALIDATION LOGIC
    if (
      localStorage.getItem("token") &&                    // Check if token exists in browser storage
      decodeURIComponent(token) &&                       // Check if URL token is valid (not null/undefined)
      localStorage.getItem("token") === decodeURIComponent(token)  // Verify URL token matches stored token
    ) {
      clearAllLocalStorage();              // Clear all browser storage data
    }
    setUser(null);                         // Reset user state to null (logged out)
    navigate("/");                         // Redirect to home page
  }, [token, navigate]);                   // Dependency array - runs when token or navigate changes

  // 4. RENDER OUTPUT
  return <Loader open={true} />;           // Display loading spinner during logout process
};

/**
 * The Main App - The "Smart Receptionist"
 * 
 * This is the main brain of our app. It's like a super-smart receptionist who:
 * - Knows who you are (user authentication)
 * - Remembers your preferences (settings, language, cookies)
 * - Shows you the right things at the right time
 * - Handles all the special requests and permissions
 */
function App() {
  // The "guest list" - who is currently logged in
  const { user } = useUserAuth();
  
  // The "building settings" - which direction text goes, which "apartment" we're in
  const { dir, tenant } = useAppSelector(getSettings);
  
  // The "privacy permission" - have you agreed to our privacy policy?
  const [cookiedAccepted, setCookieAccepted] = useState(
    Cookies.get("utilcookie") === "true"
  );
  
  // The "language translator" - sets up the right language for you
  useLocaleLanguage();
  
  // The "tour completion" - have you taken the building tour yet?
  const [walkthroughDone, SetWalkThroughDone] = useState(
    localStorage.getItem("walkthroughDone")
  );

  // The "marketing tracker" - remembers how you found us (like a referral system)
  useEffect(() => {
    if (window?.location.search?.includes("utm_source")) {
      localStorage.setItem("queryParam", window?.location.search);
    }
  }, []);

  // The "building verification" - checks if this is a valid "apartment" (tenant)
  const {
    isLoading: checkingTenant,    // Are we still checking?
    tenantNotFound,               // Is this apartment invalid?
    hubspot,                      // Customer service chat settings
  } = useCheckTenant();
  
  // The "customer service chat" - sets up the help desk
  useHubspotChat(hubspot);
  
  // The "analytics camera" - starts recording to help us improve
  useEffect(() => {
    clarity.init(process.env.REACT_APP_CLARITY_ID);
  }, []);

  // The "privacy auto-accept" - automatically accepts privacy after 5 seconds
  useEffect(() => {
    if (!cookiedAccepted) {
      setTimeout(() => {
        Cookies.set("utilcookie", "true");
        setCookieAccepted(true);
      }, 5000);
    }
  }, []);

  // The "Facebook tracking camera" - sets up Facebook's analytics (if enabled)
  useEffect(() => {
    if (tenant?.meta_pixel) {
      // Don't set up the camera twice - check if it's already there
      if (window.fbq) return;

      if (tenant?.meta_pixel?.script) {
        // Install the Facebook tracking camera
        const script = document.createElement("script");
        script.innerHTML = tenant?.meta_pixel?.script;
        document.head.appendChild(script);
      }

      if (tenant?.meta_pixel?.url) {
        // Add a backup tracking method (for when JavaScript is disabled)
        const noscript = document.createElement("noscript");
        const img = document.createElement("img");
        img.height = "1";
        img.width = "1";
        img.style.display = "none";
        img.src = tenant?.meta_pixel?.url;
        noscript.appendChild(img);
        document.head.appendChild(noscript);
      }
    }
  }, [tenant]);

  /*eslint-disable-next-line*/
  // OLD ROUTING SYSTEM (commented out) - like an old map that we replaced
  // This was the old way of deciding which page to show users
  // We now use the new Routes system below instead

  // The "building verification" - are we still checking if this is a valid apartment?
  if (checkingTenant) {
    return (
      <Loader
        open={true}
        className="bg-transparent"
        loaderClassName="!border-t-theme-blue"
      />
    );
  }

  // The "wrong address" - this apartment doesn't exist!
  if (tenantNotFound) {
    return <NotFoundPage invalidDomain />;
  }

  return (
    <>
      {/* The "building tour" - show this to new visitors who haven't taken the tour yet */}
      {!walkthroughDone || walkthroughDone === "false" ? (
        <Walkthrough SetWalkThroughDone={SetWalkThroughDone} />
      ) : (
        ""
      )}
      
      {/* The "privacy permission banner" - ask for permission to use tracking cameras */}
      {cookiedAccepted ? (
        ""
      ) : (
        <CookieConsent
          location="top"
          buttonText={t("cookieConsentAcceptText")}
          // onAccept={() => {
          //   // clarity.init(process.env.REACT_APP_CLARITY_ID);
          // }}
          // enableDeclineButton
          // declineButtonStyle={{
          //   background: "transparet",
          // }}
          // declineButtonText={t("cookieConsentRejectText")}
          cookieName="utilcookie"
          style={{
            background: "#2B373B",
            zIndex: "999999",
            width: "calc(100vw - 6px)",
            left: "3px",
            top: "3px !important",
            direction: dir,
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            marginTop: "3px",
          }}
          buttonStyle={{
            color: "#4e503b",
            fontSize: "16px",
            borderRadius: "4px",
            margin: 0,
            marginBottom: "10px",
          }}
          buttonWrapperClasses="flex flex-row-reverse"
          expires={365}
        >
          {t("cookieConsentText")}
        </CookieConsent>
      )}
      {/* The "building directory" - tells users which room to go to based on the URL */}
      <Routes>
        <Route path="/redirect/:token" element={<Redirect />} />        {/* The "welcome back" room */}
        <Route path="/logout/:token" element={<Logout />} />            {/* The "goodbye" room */}
        <Route path="/activate/:key" element={<ActivateKey />} />       {/* The "account activation" room */}
        <Route path="/quiz/:id" element={<HollandCodeQuiz />} />        {/* The "personality test" room */}
        <Route
          path="/register"
          element={user ? <Navigate to="/" /> : <Register />}
        />
        <Route
          path="/user-auth/*"
          element={user ? <Navigate to="/" /> : <UserAuth />}
        />
        <Route path="*" element={<HomeRoutes />} />                     {/* The "main building" - everything else */}
      </Routes>
      
      {/* The "message display system" - shows important notifications to users */}
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        transition="slide"
      />
    </>
  );
}

export default App;
